// frontend/app/screens/DavinciScannerScreen.js
//
// BDD 2026-06-09 - Da Vinci 3-Day Challenge kiosk scanner.
//
// Restricted to the kiosk login (role = "davinci_kiosk"; enforced server-side
// too). Flow: scan QR (USB keyboard-wedge / manual entry, or webcam) -> redeem
// the opaque code -> show activation success -> email the generated certificate
// using the separate send-certificate endpoint.

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Image,
  StyleSheet,
  Platform,
} from "react-native";
import { useAuth } from "../AuthContext";
import { FASTAPI_BASE_URL } from "../config";
import DavinciCertificate from "../components/DavinciCertificate";

const QR_READER_ID = "davinci-qr-reader";
const ROOT_ID = "dv-scan-root";

const DAVINCI_LOGO = require("../../assets/images/davinci.png");
const SARAS_LOGO = require("../../assets/Saras-3D_Logo.png");

const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Jost:wght@400;500;600&display=swap";

const injectScannerTheme = () => {
  if (typeof document === "undefined") return;
  if (!document.getElementById("dv-scan-fonts")) {
    const l = document.createElement("link");
    l.id = "dv-scan-fonts";
    l.rel = "stylesheet";
    l.href = FONTS_HREF;
    document.head.appendChild(l);
  }
  if (document.getElementById("dv-scan-style")) return;
  const s = document.createElement("style");
  s.id = "dv-scan-style";
  s.textContent = `
    #${ROOT_ID} {
      background:
        radial-gradient(60% 40% at 50% -10%, #18367a 0%, rgba(24,54,122,0) 60%),
        radial-gradient(70% 55% at 50% 120%, #142a63 0%, rgba(20,42,99,0) 55%),
        linear-gradient(160deg,#060f2c 0%,#0b1f4d 50%,#060f2c 100%) !important;
    }
    #${QR_READER_ID} { width:100%!important; height:100%!important; border:0!important; }
    #${QR_READER_ID} video { width:100%!important; height:100%!important; object-fit:cover!important; display:block; border-radius:18px; }
    #${QR_READER_ID} img { display:none!important; }
    #dv-scanline { animation: dvScan 2.6s cubic-bezier(.4,0,.2,1) infinite; }
    @keyframes dvScan { 0%{ top:8%; opacity:.2 } 50%{ top:88%; opacity:1 } 100%{ top:8%; opacity:.2 } }
    .dv-corner { animation: dvCorner 2.2s ease-in-out infinite; }
    @keyframes dvCorner { 0%,100%{ opacity:.55 } 50%{ opacity:1 } }
  `;
  document.head.appendChild(s);
};

const ALLOWED_SCAN_ROLES = new Set([
  "davinci_kiosk",
  "moderator",
  "partneradmin",
  "admin",
  "institute_admin",
  "schooladmin",
  "superadmin",
]);

const isKioskUser = (user) =>
  ALLOWED_SCAN_ROLES.has((user?.role || "").toLowerCase()) ||
  user?.is_internal_admin === true;

const getApiErrorMessage = (data, fallback) =>
  data?.detail ||
  data?.django_response_string ||
  data?.message ||
  data?.error ||
  fallback;

function DavinciScannerInner() {
  const { user, fetchWithAuth } = useAuth();

  const [manualCode, setManualCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activated, setActivated] = useState(null);
  const [certCompletion, setCertCompletion] = useState(null);
  const [certificateHtml, setCertificateHtml] = useState("");
  const [certificateSendState, setCertificateSendState] = useState({
    status: "idle",
    message: "",
  });
  const [useCamera, setUseCamera] = useState(Platform.OS === "web");
  const [showManual, setShowManual] = useState(false);

  const scannerRef = useRef(null);
  const inputRef = useRef(null);
  const handledRef = useRef(false);
  const lastCertificateSendCodeRef = useRef("");

  const authorized = isKioskUser(user);

  const activateCode = useCallback(
    async (rawCode) => {
      const code = (rawCode || "").trim();
      if (!code) return;
      setLoading(true);
      setError("");
      setCertificateSendState({ status: "idle", message: "" });
      try {
        const res = await fetchWithAuth(`${FASTAPI_BASE_URL}/davinci/redeem/confirm`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.status === 403) {
          setError(getApiErrorMessage(data, "This device is not signed in as the Da Vinci kiosk."));
          return;
        }
        if (res.status === 404) {
          setError(getApiErrorMessage(data, `No challenge found for code "${code}".`));
          return;
        }
        if (!res.ok) {
          setError(getApiErrorMessage(data, `Could not activate certificate (${res.status}). Try again.`));
          return;
        }
        setActivated({
          student_name: data.completion.student_name,
          student_email: data.completion.student_email,
          subjects: data.completion.subjects,
          completed_at: data.completion.completed_at,
          redeem_code: data.completion.redeem_code,
          already: data.status === "already_redeemed",
        });
        setCertCompletion(data.completion);
      } catch (e) {
        setError(e?.message || "Network error while activating certificate.");
      } finally {
        setLoading(false);
        setManualCode("");
      }
    },
    [fetchWithAuth]
  );

  const resetForNext = useCallback(() => {
    setError("");
    setActivated(null);
    setCertCompletion(null);
    setCertificateHtml("");
    setCertificateSendState({ status: "idle", message: "" });
    setManualCode("");
    lastCertificateSendCodeRef.current = "";
    if (Platform.OS === "web") {
      setShowManual(false);
      setUseCamera(true);
    }
  }, []);


  useEffect(() => {
    const redeemCode = certCompletion?.redeem_code;
    const studentEmail = certCompletion?.student_email;
    const studentName = certCompletion?.student_name;
    if (!redeemCode || !studentEmail || !certificateHtml) return;
    if (lastCertificateSendCodeRef.current === redeemCode) return;

    let cancelled = false;
    lastCertificateSendCodeRef.current = redeemCode;
    setCertificateSendState({ status: "sending", message: "" });

    (async () => {
      try {
        const res = await fetchWithAuth(`${FASTAPI_BASE_URL}/davinci/send-certificate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: studentEmail,
            full_name: studentName,
            certificate_html: certificateHtml,
          }),
        });

        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !data?.email_sent) {
          const detail = getApiErrorMessage(data, "Certificate email could not be sent.");
          setCertificateSendState({ status: "error", message: detail });
          lastCertificateSendCodeRef.current = "";
          return;
        }

        setCertificateSendState({
          status: "sent",
          message: `Certificate emailed to ${studentEmail}.`,
        });
      } catch (e) {
        if (cancelled) return;
        setCertificateSendState({
          status: "error",
          message: e?.message || "Network error while sending certificate email.",
        });
        lastCertificateSendCodeRef.current = "";
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [certCompletion, certificateHtml, fetchWithAuth]);

  useEffect(() => {
    if (!activated) return;
    if (!["sent", "error"].includes(certificateSendState.status)) return;
    const t = setTimeout(() => resetForNext(), 4000);
    return () => clearTimeout(t);
  }, [activated, certificateSendState.status, resetForNext]);

  useEffect(() => {
    if (Platform.OS !== "web" || !useCamera || !authorized) return;
    let cancelled = false;
    handledRef.current = false;
    let instance = null;

    const safeStop = async (s) => {
      if (!s) return;
      try {
        await s.stop();
      } catch {}
    };

    (async () => {
      try {
        const mod = require("html5-qrcode");
        const Html5Qrcode = mod.Html5Qrcode;
        instance = new Html5Qrcode(QR_READER_ID);
        scannerRef.current = instance;
        await instance.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          async (decodedText) => {
            if (cancelled || handledRef.current) return;
            handledRef.current = true;
            await safeStop(instance);
            if (cancelled) return;
            setUseCamera(false);
            activateCode(decodedText);
          },
          () => {}
        );
      } catch (e) {
        if (!cancelled) {
          setError("Could not start the camera. Enter the code manually instead.");
          setUseCamera(false);
          setShowManual(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      const s = scannerRef.current;
      scannerRef.current = null;
      safeStop(s);
    };
  }, [useCamera, authorized, activateCode]);

  useEffect(() => {
    if (Platform.OS === "web") injectScannerTheme();
  }, []);

  const Brand = () => (
    <View style={styles.brand}>
      <Image source={DAVINCI_LOGO} style={styles.brandLogoDv} resizeMode="contain" />
      <View style={styles.brandDivider} />
      <Image source={SARAS_LOGO} style={styles.brandLogoSaras} resizeMode="contain" />
    </View>
  );

  if (!authorized) {
    return (
      <View nativeID={ROOT_ID} style={styles.centered}>
        <Brand />
        <Text style={styles.guardTitle}>Kiosk or moderator login required</Text>
        <Text style={styles.guardBody}>
          Sign in with the Da Vinci kiosk account or a moderator/admin account to use the scanner.
        </Text>
      </View>
    );
  }

  if (activated) {
    return (
      <ScrollView nativeID={ROOT_ID} contentContainerStyle={styles.container}>
        <Brand />
        {certificateSendState.status === "sending" ? (
          <View style={[styles.banner, styles.bannerWarn, styles.successBanner]}>
            <Text style={styles.bannerWarnText}>Sending certificate email...</Text>
          </View>
        ) : null}
        {certificateSendState.status === "sent" ? (
          <View style={[styles.banner, styles.bannerOk, styles.successBanner]}>
            <Text style={styles.bannerOkText}>{certificateSendState.message}</Text>
          </View>
        ) : null}
        {certificateSendState.status === "error" ? (
          <View style={[styles.banner, styles.bannerError, styles.successBanner]}>
            <Text style={styles.bannerErrorText}>{certificateSendState.message}</Text>
          </View>
        ) : null}
        <View style={[styles.card, styles.successCard]}>
          <Text style={styles.successCheck}>+</Text>
          <Text style={styles.successTitle}>
            {activated.already ? "Already activated" : "Activated"}
          </Text>
          <Text style={styles.successBody}>
            {activated.student_name}&apos;s certificate is now on the display.
          </Text>
          <Text style={styles.detailMuted}>Code: {activated.redeem_code}</Text>

          <View style={styles.row}>
            <TouchableOpacity style={[styles.btn, styles.btnGold]} onPress={resetForNext}>
              <Text style={styles.btnGoldText}>Scan next</Text>
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.hiddenCertCapture}>
          <DavinciCertificate
            studentName={certCompletion?.student_name || activated.student_name}
            subjects={certCompletion?.subjects || activated.subjects || []}
            completedAt={certCompletion?.completed_at || activated.completed_at}
            code={certCompletion?.redeem_code || activated.redeem_code}
            onHtmlReady={setCertificateHtml}
          />
        </View>
      </ScrollView>
    );
  }

  const showManualEntry = showManual || !useCamera || Platform.OS !== "web";

  return (
    <ScrollView nativeID={ROOT_ID} contentContainerStyle={styles.container}>
      <Brand />
      <Text style={styles.eyebrow}>3-DAY CHALLENGE</Text>
      <Text style={styles.h1}>Certificate Station</Text>
      <Text style={styles.h2}>Scan a participant&apos;s QR code to add it to the certificate display</Text>

      <View style={styles.card}>
        {Platform.OS === "web" && useCamera && (
          <>
            <View style={styles.viewport}>
              <View nativeID={QR_READER_ID} style={styles.qrReader} />
              <View nativeID="dv-scanline" style={styles.scanLine} pointerEvents="none" />
              <View style={[styles.corner, styles.cornerTL]} pointerEvents="none" />
              <View style={[styles.corner, styles.cornerTR]} pointerEvents="none" />
              <View style={[styles.corner, styles.cornerBL]} pointerEvents="none" />
              <View style={[styles.corner, styles.cornerBR]} pointerEvents="none" />
            </View>
            <Text style={styles.viewportHint}>Hold the QR code steady inside the frame</Text>
          </>
        )}

        {showManualEntry && (
          <View style={styles.manualWrap}>
            <Text style={styles.label}>Enter or scan the code</Text>
            <TextInput
              ref={inputRef}
              style={styles.input}
              value={manualCode}
              onChangeText={setManualCode}
              onSubmitEditing={() => activateCode(manualCode)}
              placeholder="DV-XXXXXX"
              placeholderTextColor="#6f7fb0"
              autoFocus
              autoCapitalize="characters"
              autoCorrect={false}
              returnKeyType="search"
              editable={!loading}
            />
            <TouchableOpacity
              style={[styles.btn, styles.btnGold, { marginTop: 12 }]}
              onPress={() => activateCode(manualCode)}
              disabled={loading || !manualCode.trim()}
            >
              <Text style={styles.btnGoldText}>Activate Certificate</Text>
            </TouchableOpacity>
            <Text style={styles.hint}>A USB QR scanner types the code here and activates it automatically.</Text>
          </View>
        )}

        {Platform.OS === "web" && (
          <View style={styles.row}>
            <TouchableOpacity
              style={[styles.btn, styles.btnGhost]}
              onPress={() => setUseCamera((v) => !v)}
              disabled={loading}
            >
              <Text style={styles.btnGhostText}>{useCamera ? "Stop camera" : "Start camera"}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.btn, styles.btnGhost]}
              onPress={() => setShowManual((v) => !v)}
              disabled={loading}
            >
              <Text style={styles.btnGhostText}>
                {showManual ? "Hide manual entry" : "Enter code manually"}
              </Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {loading && <ActivityIndicator size="large" color="#e7c46a" style={{ marginTop: 16 }} />}

      {!!error && (
        <View style={[styles.banner, styles.bannerError]}>
          <Text style={styles.bannerErrorText}>{error}</Text>
        </View>
      )}
    </ScrollView>
  );
}

class ScannerErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error("[DavinciScanner] error boundary caught:", error, info);
  }
  reset = () => this.setState({ hasError: false });
  render() {
    if (this.state.hasError) {
      return (
        <View nativeID={ROOT_ID} style={styles.centered}>
          <Text style={styles.guardTitle}>Something went wrong</Text>
          <Text style={styles.guardBody}>
            The scanner hit an error. Tap below to reset and try again.
          </Text>
          <TouchableOpacity style={[styles.btn, styles.btnPrimary, { marginTop: 16 }]} onPress={this.reset}>
            <Text style={styles.btnPrimaryText}>Reset scanner</Text>
          </TouchableOpacity>
        </View>
      );
    }
    return this.props.children;
  }
}

export default function DavinciScannerScreen() {
  return (
    <ScannerErrorBoundary>
      <DavinciScannerInner />
    </ScannerErrorBoundary>
  );
}

const FONT_DISPLAY = '"Cormorant Garamond", Georgia, serif';
const FONT_LABEL = '"Jost", system-ui, sans-serif';
const GOLD = "#e7c46a";

const styles = StyleSheet.create({
  container: { paddingVertical: 36, paddingHorizontal: 24, alignItems: "center", maxWidth: 760, alignSelf: "center", width: "100%", minHeight: "100%" },
  centered: { flex: 1, minHeight: "100%", alignItems: "center", justifyContent: "center", padding: 32 },

  brand: { flexDirection: "row", alignItems: "center", justifyContent: "center", marginBottom: 18 },
  brandLogoDv: { height: 46, width: 140 },
  brandLogoSaras: { height: 38, width: 140 },
  brandDivider: { width: 1, height: 32, backgroundColor: "rgba(231,196,106,0.45)", marginHorizontal: 16 },

  eyebrow: { fontFamily: FONT_LABEL, fontSize: 12, letterSpacing: 5, fontWeight: "600", color: GOLD, textTransform: "uppercase", marginTop: 2 },
  h1: { fontFamily: FONT_DISPLAY, fontSize: 38, fontWeight: "700", color: "#f5f1e3", letterSpacing: 1, marginTop: 2, textAlign: "center" },
  h2: { fontSize: 15, color: "#9fb0d8", marginBottom: 24, marginTop: 4, textAlign: "center" },

  guardTitle: { fontFamily: FONT_DISPLAY, fontSize: 30, fontWeight: "700", color: "#f5f1e3", marginBottom: 8, textAlign: "center" },
  guardBody: { fontSize: 15, color: "#9fb0d8", textAlign: "center", maxWidth: 380, lineHeight: 22 },

  card: {
    width: "100%",
    backgroundColor: "rgba(15,33,77,0.55)",
    borderRadius: 20,
    padding: 22,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "rgba(231,196,106,0.28)",
    shadowColor: "#000",
    shadowOpacity: 0.35,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 16 },
  },

  viewport: {
    width: "100%",
    maxWidth: 340,
    aspectRatio: 1,
    alignSelf: "center",
    borderRadius: 20,
    overflow: "hidden",
    backgroundColor: "#04102e",
    borderWidth: 1,
    borderColor: "rgba(231,196,106,0.35)",
    position: "relative",
  },
  qrReader: { position: "absolute", top: 0, left: 0, right: 0, bottom: 0 },
  scanLine: {
    position: "absolute",
    left: "8%",
    right: "8%",
    top: "8%",
    height: 2,
    backgroundColor: GOLD,
    borderRadius: 2,
    shadowColor: GOLD,
    shadowOpacity: 0.9,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 0 },
  },
  corner: { position: "absolute", width: 28, height: 28, borderColor: GOLD },
  cornerTL: { top: 12, left: 12, borderTopWidth: 3, borderLeftWidth: 3, borderTopLeftRadius: 6 },
  cornerTR: { top: 12, right: 12, borderTopWidth: 3, borderRightWidth: 3, borderTopRightRadius: 6 },
  cornerBL: { bottom: 12, left: 12, borderBottomWidth: 3, borderLeftWidth: 3, borderBottomLeftRadius: 6 },
  cornerBR: { bottom: 12, right: 12, borderBottomWidth: 3, borderRightWidth: 3, borderBottomRightRadius: 6 },
  viewportHint: { fontFamily: FONT_LABEL, fontSize: 12.5, color: "#9fb0d8", textAlign: "center", marginTop: 14, letterSpacing: 0.5 },

  manualWrap: { width: "100%" },
  label: { fontFamily: FONT_LABEL, fontSize: 12, fontWeight: "600", color: "#c3cfee", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 },
  input: {
    borderWidth: 1,
    borderColor: "rgba(231,196,106,0.4)",
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    fontSize: 22,
    letterSpacing: 3,
    color: "#f4f7ff",
    backgroundColor: "rgba(255,255,255,0.06)",
    textAlign: "center",
  },
  row: { flexDirection: "row", gap: 12, marginTop: 16, flexWrap: "wrap", justifyContent: "center" },
  hint: { fontSize: 12, color: "#7e8fc0", marginTop: 10, textAlign: "center" },

  btn: { paddingVertical: 13, paddingHorizontal: 22, borderRadius: 12 },
  btnGold: { backgroundColor: GOLD },
  btnGoldText: { color: "#0b2a63", fontWeight: "800", fontSize: 15, letterSpacing: 0.3 },
  btnPrimary: { backgroundColor: "#13347a" },
  btnPrimaryText: { color: "#fff", fontWeight: "700", fontSize: 15 },
  btnGhost: { backgroundColor: "rgba(255,255,255,0.08)", borderWidth: 1, borderColor: "rgba(231,196,106,0.3)" },
  btnGhostText: { color: "#e7e9f2", fontWeight: "600", fontSize: 15 },

  banner: { borderRadius: 12, paddingVertical: 11, paddingHorizontal: 14, marginBottom: 14 },
  bannerError: { backgroundColor: "rgba(220,38,38,0.18)", borderWidth: 1, borderColor: "rgba(248,113,113,0.4)", marginTop: 16, width: "100%" },
  bannerErrorText: { color: "#fecaca", fontWeight: "600", fontSize: 14 },
  bannerWarn: { backgroundColor: "rgba(245,158,11,0.18)", borderWidth: 1, borderColor: "rgba(251,191,36,0.4)" },
  bannerWarnText: { color: "#fde68a", fontWeight: "600", fontSize: 14 },
  bannerOk: { backgroundColor: "rgba(34,197,94,0.16)", borderWidth: 1, borderColor: "rgba(74,222,128,0.4)" },
  bannerOkText: { color: "#bbf7d0", fontWeight: "700", fontSize: 14 },

  successCard: { alignItems: "center" },
  successCheck: { fontSize: 56, color: "#4ade80", marginBottom: 6, fontWeight: "800" },
  successTitle: { fontFamily: FONT_DISPLAY, fontSize: 30, fontWeight: "700", color: "#f5f1e3", marginBottom: 10, textAlign: "center" },
  successBody: { fontSize: 16, color: "#c3cfee", textAlign: "center", marginBottom: 6, lineHeight: 22 },
  successBanner: { width: "100%", maxWidth: 760 },
  hiddenCertCapture: {
    position: "absolute",
    width: 1,
    height: 1,
    opacity: 0,
    overflow: "hidden",
    left: -9999,
    top: -9999,
  },
});
